import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSmartphone, FiDollarSign, FiClock, FiActivity, FiLayers, FiAlertCircle } = FiIcons;

const PainAmplification = () => {
  const problems = [
    {
      icon: FiLayers,
      title: "Juggling 7 Different Apps",
      description: "Instagram on your phone. Facebook on desktop. LinkedIn elsewhere. TikTok native. Twitter via... what was that tool? You're constantly context-switching and it's killing your focus.",
      color: "bg-red-50 text-red-600",
      illustration: (
        <div className="relative w-full h-32 flex items-center justify-center">
            <div className="w-16 h-16 bg-brand-navy rounded-full flex items-center justify-center text-white z-10 relative">
                <span className="text-2xl font-bold">😤</span>
            </div>
            {[0, 60, 120, 180, 240, 300].map((deg, i) => (
                <div key={i} className="absolute w-8 h-8 rounded-lg bg-gray-100 shadow-sm flex items-center justify-center text-gray-400" style={{ transform: `rotate(${deg}deg) translate(60px) rotate(-${deg}deg)` }}>
                    <div className="w-4 h-4 rounded-full bg-current opacity-50"></div>
                </div>
            ))}
        </div>
      )
    },
    {
      icon: FiDollarSign,
      title: "Tools Cost Too Much",
      description: "Hootsuite: $99/month per user. Buffer: Hit limits constantly. Sprout Social: $199/month minimum. Hire an agency? $2,000+/month. And none include viral growth tools.",
      color: "bg-orange-50 text-orange-600",
      illustration: (
        <div className="relative w-full h-32 flex items-center justify-center">
             <div className="text-6xl animate-bounce">💸</div>
             <div className="absolute top-0 right-10 text-2xl opacity-50">$99</div>
             <div className="absolute bottom-0 left-10 text-2xl opacity-50">$199</div>
        </div>
      )
    },
    {
      icon: FiClock,
      title: "Manual Posting is Soul-Crushing",
      description: "Set 5 alarms daily to post at 'optimal times.' Drop everything you're doing. Open app. Write caption. Add hashtags. Schedule. Repeat 3-5x per day. Every. Single. Day.",
      color: "bg-yellow-50 text-yellow-600",
      illustration: (
        <div className="relative w-full h-32 flex items-center justify-center">
             <div className="w-24 h-24 rounded-full border-4 border-brand-navy flex items-center justify-center relative">
                 <div className="absolute top-1/2 left-1/2 w-1 h-8 bg-brand-navy origin-bottom transform -translate-x-1/2 -translate-y-full rotate-45"></div>
                 <div className="absolute top-1/2 left-1/2 w-1 h-6 bg-brand-navy origin-bottom transform -translate-x-1/2 -translate-y-full -rotate-90"></div>
                 <div className="text-2xl z-10">😫</div>
             </div>
        </div>
      )
    }
  ];

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-brand-navy mb-6">
            You're Not Lazy. Social Media is Just Exhausting.
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Most entrepreneurs spend 12-15 hours per week on social media.
            That's 780 hours per year. Here's why:
          </p>
        </motion.div>

        {/* Problem Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-white rounded-2xl p-8 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 group flex flex-col"
            >
              {/* Illustration Area */}
              <div className="mb-8 bg-gray-50 rounded-xl p-4 group-hover:bg-gray-100 transition-colors">
                {problem.illustration}
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-brand-navy mb-4 flex items-center gap-2">
                 {problem.title}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm flex-grow">
                {problem.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Transition */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-brand-teal">
            "What if you could post once on Sunday... and be done for the week?"
          </h3>
        </motion.div>
      </div>
    </section>
  );
};

export default PainAmplification;